// @flow

import * as React from 'react';
import cx from 'classnames';

import SearchActionCreators from 'search/actions/SearchActionCreators';
import EditRelevanceLink, {
  editRelevanceLinkWrapperClass
} from './EditRelevanceLink';

import type { VenueResult, HighlightCapacityType } from 'search/types';
import {
  BasicVenueCard,
  FeaturedVenueCard
} from 'common/components/VenueCard.js';

type CommonProps = {|
  venue: VenueResult,
  openInNewTab: boolean,
  isActive?: boolean,
  onClickVenueLink?: () => void,
  highlightCapacity: HighlightCapacityType
|};

type BasicVariantProps = {|
  ...CommonProps,
  variant: 'BASIC',
  features?: { edit_relevance?: boolean, ... }
|};

type FeaturedVariantProps = {|
  ...CommonProps,
  variant: 'FEATURED',
  priority: 'REGULAR' | 'HIGH'
|};

export type Props = BasicVariantProps | FeaturedVariantProps;

function handleFocus(venueId: number) {
  SearchActionCreators.focusSearchResult(venueId);
}

export default function VenuePreview(props: Props): React.Node {
  const {
    venue,
    openInNewTab = true,
    isActive,
    onClickVenueLink,
    highlightCapacity = 'SITTING'
  } = props;

  let warning: 'unpublished' | 'restricted' | void;
  if (!venue.published) {
    warning = 'unpublished';
  } else if (venue.restricted) {
    warning = 'restricted';
  }

  const commonVenueData = {
    capacity_sitting: venue.capacity_sitting,
    capacity_standing: venue.capacity_standing,
    city: venue.city,
    has_availability_data: venue.has_availability_data,
    id: venue.id,
    locale: venue.locale,
    reviews_average: venue.reviews_average,
    reviews_count: venue.reviews_count,
    title: venue.title,
    url: venue.url,
    preview_images: venue.preview_images,
    rent_per_day: venue.rent_per_day,
    rent_per_person: venue.rent_per_person,
    rent_per_hour: venue.rent_per_hour,
    sales_guarantee: venue.sales_guarantee,
    warning
  };

  const focusHandlers = {
    onFocus: () => handleFocus(venue.id),
    onMouseEnter: () => handleFocus(venue.id)
  };

  if (props.variant === 'FEATURED') {
    const featuredProps: FeaturedVariantProps = (props: FeaturedVariantProps);
    return (
      <div {...focusHandlers}>
        <FeaturedVenueCard
          venue={commonVenueData}
          priority={featuredProps.priority}
          onClickVenueLink={onClickVenueLink}
          highlightCapacity={highlightCapacity}
          openInNewTab={openInNewTab}
        />
      </div>
    );
  }

  const previewBoxClass = cx({
    'b-search-result': true,
    'is-active': isActive
  });

  return (
    <li {...focusHandlers} className={editRelevanceLinkWrapperClass}>
      <div className={previewBoxClass} data-testid="regular-search-result">
        <BasicVenueCard
          venue={commonVenueData}
          openInNewTab={openInNewTab}
          onClickVenueLink={onClickVenueLink}
          highlightCapacity={highlightCapacity}
        />
      </div>
      {props.features?.edit_relevance && (
        <EditRelevanceLink venueId={venue.id} />
      )}
    </li>
  );
}
